import { createFileRoute, useNavigate, useRouteContext } from "@tanstack/react-router";
import { useAtom } from "jotai";
import { useCallback, useMemo, useState, type JSX } from "react";
import { z } from "zod";

import { graphql } from "@repo/graphql-types";
import {
  GetAppointmentTypeReviewPageQueryDocument,
  HomeSectionNextStepsQueryDocument,
  SchedulesPageQueryDocument,
} from "@repo/graphql-types/graphql";
import CalendarScheduleStethoscope from "@repo/icons/default/CalendarScheduleStethoscope";

import { FirstScheduleFooter } from "@/components/onboarding/first-schedule-footer";
import {
  MessageDrawerActionButton,
  MessageDrawerActions,
  MessageDrawerBody,
  MessageDrawerRoot,
  MessageDrawerTitle,
} from "@/components/ui/drawer/message-drawer";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import {
  LocationDayOfWeekTimesInput,
  type LocationTimes,
} from "@/components/ui/input/location-day-of-week-times-input";
import { Page, PageTitle } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { useGraphQLMutationWithErrorHandler, useResetQuery } from "@/hooks/use-graphql";
import { firstScheduleAtom, hasLocationTimes } from "@/lib/atoms/first-schedule-atom";
import {
  FIRST_SCHEDULE_STEPS,
  getFirstScheduleProgress,
} from "@/lib/helpers/first-schedule-flow";
import { groupDaysByTime } from "@/lib/helpers/group-days-by-time";

const CriarPrimeiraAgendaUsuarioMutation = graphql(`
  mutation CriarPrimeiraAgendaUsuarioMutation($input: CriarPrimeiraAgendaUsuarioInput!) {
    criarPrimeiraAgendaUsuario(input: $input) {
      criarPrimeiraAgendaUsuarioOutput {
        codAgendas
      }
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
    }
  }
`);

const scheduleSearchSchema = z.object({
  codUnidade: z.coerce.number(),
  nomeLimpo: z.string(),
  sigla: z.string(),
  returnTo: z.literal("appointment-type").optional(),
  codAgendas: z.array(z.coerce.number()).optional(),
});

// Backend uses TimeSpan; serializer expects "HH:mm:ss".
const toTimeSpan = (time: string): string => `${time}:00`;

const FirstScheduleSchedulePage = (): JSX.Element => {
  const navigate = useNavigate();
  const { flags } = useRouteContext({ from: "__root__" });
  const resetAppointmentTypeReviewQuery = useResetQuery(
    GetAppointmentTypeReviewPageQueryDocument,
  );
  const resetHomeNextStepsQuery = useResetQuery(HomeSectionNextStepsQueryDocument);
  const resetSchedulesQuery = useResetQuery(SchedulesPageQueryDocument);
  const search = Route.useSearch();
  const [form, setForm] = useAtom(firstScheduleAtom);

  const { codUnidade, nomeLimpo, returnTo, codAgendas } = search;
  const [showConfirmationDrawer, setShowConfirmationDrawer] = useState(false);

  const showsAppointmentTypesSteps = useMemo(
    () => flags["exibe-configuracao-tipos-de-atendimento-na-primeira-agenda"],
    [flags],
  );

  const { mutateAsync: criarPrimeiraAgendaMutateAsync, isPending } =
    useGraphQLMutationWithErrorHandler(CriarPrimeiraAgendaUsuarioMutation);

  const handleLocationTimesChange = useCallback(
    (values: LocationTimes[]): void => {
      setForm((prev) => ({ ...prev, locationTimes: values }));
    },
    [setForm],
  );

  const handleSubmit = async (): Promise<void> => {
    const groups = groupDaysByTime(form.locationTimes);
    if (groups.length === 0) return;

    const response = await criarPrimeiraAgendaMutateAsync({
      input: {
        codUnidade,
        agendas: groups.map((group, index) => ({
          inicio: toTimeSpan(group.startTime),
          fim: toTimeSpan(group.endTime),
          diasSemana: group.daysOfWeek,
          codUsuarioAgenda: codAgendas?.[index] ?? undefined,
        })),
      },
    });

    const output = response.criarPrimeiraAgendaUsuario.criarPrimeiraAgendaUsuarioOutput;
    const codAgendasCriadas = output?.codAgendas ?? [];

    setForm((prev) => ({
      ...prev,
      codAgendas: codAgendasCriadas,
    }));

    resetHomeNextStepsQuery();
    resetSchedulesQuery();

    if (returnTo === "appointment-type") {
      resetAppointmentTypeReviewQuery();

      void navigate({
        to: "/settings/schedule-options/appointment-types/create/review",
        search: { codAgendas: codAgendasCriadas },
      });

      return;
    }

    void navigate({
      to: showsAppointmentTypesSteps
        ? "/onboarding/first-schedule/first-appointment"
        : "/onboarding/first-schedule/success",
    });
  };

  const submitSchedule = (): void => {
    void handleSubmit();
  };

  return (
    <RoundedLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton />
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar
          progress={getFirstScheduleProgress(
            FIRST_SCHEDULE_STEPS.schedule,
            showsAppointmentTypesSteps,
          )}
        />
      </FlowHeader>

      <Page
        className="flex flex-col justify-between pb-0 px-6"
        showCustomerIoContainer={false}
      >
        <div className="flex flex-col gap-4">
          <PageTitle>Em quais dias e horários seus pacientes podem agendar?</PageTitle>
          <LocationDayOfWeekTimesInput
            locations={[{ codUnidade, nome: nomeLimpo }]}
            initialTimes={form.locationTimes}
            onChange={handleLocationTimesChange}
            showDeleteButton={false}
          />
        </div>

        <FirstScheduleFooter
          onSubmit={() => {
            if (returnTo === "appointment-type") {
              setShowConfirmationDrawer(true);
              return;
            }

            submitSchedule();
          }}
          disabled={!hasLocationTimes(form) || isPending}
          loading={isPending}
        />
      </Page>

      <MessageDrawerRoot
        icon={CalendarScheduleStethoscope}
        variant="secondary"
        open={showConfirmationDrawer}
        setOpen={setShowConfirmationDrawer}
      >
        <MessageDrawerTitle>Criar agenda</MessageDrawerTitle>
        <MessageDrawerBody>
          {`Deseja criar a agenda na unidade ${nomeLimpo} nesses horários?`}
        </MessageDrawerBody>
        <MessageDrawerActions>
          <MessageDrawerActionButton
            onClick={() => {
              setShowConfirmationDrawer(false);
              submitSchedule();
            }}
          >
            Confirmar
          </MessageDrawerActionButton>
        </MessageDrawerActions>
      </MessageDrawerRoot>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/onboarding/first-schedule/schedule")({
  validateSearch: scheduleSearchSchema,
  component: FirstScheduleSchedulePage,
});
